import React, { useEffect, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { IndeterminateCheckbox } from '@components/common/react-table/Table';
import { SortOrder } from '@constants/common.constants';
import { ActionPopover } from '../components/ActionPopover/ActionPopover';
import { TICKET_LIST_FIELDS, TICKET_LIST_PAGE_INFO, TICKET_LIST_SORT_FIELDS } from '../constants/ticketlist.constants';
import { ITicket, IuseTicketTableProps } from '../constants/ticketlist.interface';
import {
    getItemFromSessionStorage,
    getPath,
    getViewDetailUrl,
    refreshTicketList,
    setItemToSessionStorage,
} from '../constants/ticketlist.utils';
import { useApiHandler } from '../hooks/useApiHandler';

interface TicketRow {
    original: ITicket & { isCheckboxDisabled?: boolean };
    isSelected: boolean;
    toggleRowSelected: (selected?: boolean) => void;
}

interface TicketHeaderContext {
    rows: TicketRow[];
    state: {
        selectedRowIds: Record<string, boolean>;
    };
}

const StyledDiv = styled.div`
    max-width: 10rem;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
`;

const PRIORITY_ICONS = {
    Low: getPath('plow.png'),
    Medium: getPath('pmedium.png'),
    High: getPath('phigh.png'),
    Critical: getPath('pcritical.png'),
};

export const useTicketTable = (props: IuseTicketTableProps) => {
    const {
        onCompleteTicketOpen,
        onCompleteVerifyTicketOpen,
        onReassignTicketOpen,
        onCancelTicketOpen,
        onReopenTicketOpen,
        onAutoVerificationOpen,
        isAdvanceSearch,
        viewTicketPermission,
    } = props;

    const dispatch = useDispatch();
    const { filteredListAPI, listAPI, defaultListAPI } = useApiHandler();
    const sessionPageInfo = getItemFromSessionStorage(TICKET_LIST_PAGE_INFO) || {};

    const {
        tickets: { searchCriteriaJson },
        listApiType,
    } = useSelector((state: any) => state.ticketMgmt.ticketsList) || {};
    const initialRender = useRef(true);

    useEffect(() => {
        initialRender.current = false;
    }, []);

    const fetchTickets = async (nextPageInfo: any) => {
        const pageInfo = { ...nextPageInfo };
        if (['id', 'Default', 'stdName'].includes(pageInfo.sortField)) {
            pageInfo.sortField = '';
            pageInfo.sortOrder = '';
        }
        if (pageInfo.sortField === TICKET_LIST_FIELDS.ticketId) {
            pageInfo.sortField = TICKET_LIST_SORT_FIELDS.ticketId;
        }
        setItemToSessionStorage(TICKET_LIST_PAGE_INFO, {
            ...pageInfo,
            sortField: pageInfo.sortField || sessionPageInfo.sortField,
            sortOrder: pageInfo.sortOrder || sessionPageInfo.sortOrder,
        });

        refreshTicketList({
            dispatch,
            listApiType,
            defaultListAPI,
            listAPI,
            filteredListAPI,
        });
    };

    const onSort = async (sortData: { id: string; desc: boolean }[]) => {
        const pageInfo = getItemFromSessionStorage(TICKET_LIST_PAGE_INFO) || {};
        if (sortData && sortData[0]) {
            const nextPageInfo = {
                ...pageInfo,
                currentPage: 1,
                sortField: sortData[0].id,
                sortOrder: sortData[0].desc ? SortOrder.DESC : SortOrder.ASC,
            };
            if (nextPageInfo.sortField !== pageInfo.sortField || nextPageInfo.sortOrder !== pageInfo.sortOrder) {
                if (!initialRender.current) {
                    fetchTickets(nextPageInfo);
                }
            }
        }
    };

    const onPagination = async (nextPage: { page: number }) => {
        const pageInfo = getItemFromSessionStorage(TICKET_LIST_PAGE_INFO) || {};
        const nextPageInfo = { ...pageInfo, currentPage: nextPage.page };
        fetchTickets(nextPageInfo);
    };

    const columns = useMemo(
        () => [
            {
                id: 'selector',
                width: '50px',
                Header: (table: TicketHeaderContext) => {
                    const selectableRows = table?.rows.filter((row: TicketRow) => !row.original.isCheckboxDisabled);
                    const allRowsCount = selectableRows.length;
                    const selectedRowsCount = selectableRows.filter((row: TicketRow) => row.isSelected).length;
                    const isChecked = selectedRowsCount > 0 && selectedRowsCount === allRowsCount;

                    const isIndeterminate = selectedRowsCount > 0 && selectedRowsCount < allRowsCount;

                    const handleSelectAllChange = () => {
                        const rows = table?.rows;
                        const allSelected = selectedRowsCount === allRowsCount;

                        rows.forEach((row: TicketRow) => {
                            if (row.original.isCheckboxDisabled) return;
                            row.toggleRowSelected(!allSelected);
                        });
                    };
                    return (
                        <>
                            <label className="sr-only" htmlFor="selectAll">
                                <FormattedMessage id="ticketList.table.header.selectAll" />
                            </label>
                            <IndeterminateCheckbox
                                {...{
                                    id: 'selectAll',
                                    'data-testid': 'select-all-checkbox',
                                    isChecked,
                                    indeterminate: isIndeterminate,
                                    onChange: handleSelectAllChange,
                                }}
                            />
                        </>
                    );
                },
                Cell: ({ row }: any) => {
                    return (
                        <IndeterminateCheckbox
                            {...{
                                'data-testid': `select-${row.id}`,
                                isChecked: row.isSelected,
                                isDisabled: row.original.isCheckboxDisabled,
                                onChange: () => row.toggleRowSelected(),
                                'aria-label': 'Select Ticket',
                            }}
                        />
                    );
                },
                show: viewTicketPermission,
                isSortEnabled: false,
            },
            {
                id: 'ticketId',
                Header: 'ticketList.table.header.ticket',
                Cell: ({ row }) => (
                    <a href={getViewDetailUrl(row?.original?.encryptedId)}>
                        {row.original.number} {row.original.subject}
                    </a>
                ),
                accessor: 'id',
            },
            {
                id: 'assignee',
                Header: 'ticketList.table.header.assignee',
                accessor: 'assignee',
            },
            {
                id: 'role',
                Header: 'ticketList.table.header.requestedBy',
                accessor: 'requestorFullName',
            },
            {
                Header: 'ticketList.table.header.createdfor',
                accessor: 'user',
                isSortEnabled: false,
                cellStyle: {
                    wordBreak: 'break-word',
                },
            },
            {
                id: 'tkmQueues',
                Header: 'ticketList.table.header.workgroup',
                accessor: 'queueName',
                Cell: ({ row }) => <StyledDiv title={row.original.queueName}>{row.original.queueName}</StyledDiv>,
            },
            {
                Header: 'ticketList.table.header.ticketType',
                accessor: 'ticketType',
                isSortEnabled: false,
            },
            {
                Header: 'ticketList.table.header.status',
                accessor: 'status',
            },
            {
                id: 'priority_order',
                Header: 'ticketList.table.header.p',
                accessor: 'priority',
                Cell: ({ row }) => {
                    return {
                        Low: <img src={PRIORITY_ICONS.Low} width="25px" height="25px" alt="Low" />,
                        Medium: <img src={PRIORITY_ICONS.Medium} width="25px" height="25px" alt="Medium" />,
                        High: <img src={PRIORITY_ICONS.High} width="25px" height="25px" alt="High" />,
                        Critical: <img src={PRIORITY_ICONS.Critical} width="25px" height="25px" alt="Critical" />,
                    }[row.original.priority];
                },
            },
            {
                id: 'creation_timestamp',
                Header: 'ticketList.table.header.dateOfRequest',
                accessor: 'creationTime',
            },
            {
                id: 'due_date',
                Header: 'ticketList.table.header.dueDate',
                accessor: 'dueDate',
            },
            {
                Header: () => (
                    <span className="usa-sr-only">
                        <FormattedMessage id="common.actions" />
                    </span>
                ),
                accessor: 'action',
                Cell: ({ row }) => {
                    const ticket = row.original;

                    return (
                        <ActionPopover
                            ticket={ticket}
                            onCompleteTicketOpen={onCompleteTicketOpen}
                            onCompleteVerifyTicketOpen={onCompleteVerifyTicketOpen}
                            onReassignTicketOpen={onReassignTicketOpen}
                            onCancelTicketOpen={onCancelTicketOpen}
                            onReopenTicketOpen={onReopenTicketOpen}
                            onAutoVerificationOpen={onAutoVerificationOpen}
                            isAdvanceSearch={isAdvanceSearch}
                        />
                    );
                },
                isSortEnabled: false,
                show: viewTicketPermission,
            },
        ],
        [viewTicketPermission],
    );

    return {
        columns,
        onPagination,
        onSort,
        sessionPageInfo,
    };
};
